
/// <reference path="../types/turtledove.d.ts" />
/// <reference path="../types/webappsec-permissions-policy.d.ts" />
/// <reference path="../types/topics.d.ts" />

enum AvEventTypes {
    Visit = 'Visit',
    Consent = 'Consent',
    Interact = 'Interact',
    Convert = 'Convert',
    Topics = 'Topics',
}

interface AvEvent {
    readonly type: AvEventTypes;
    readonly clientName: string | undefined;
    readonly recommendationId: string | undefined;
}

interface AvVisitEvent extends AvEvent {
    readonly location: string;
}

interface AvConsentEvent extends AvEvent {
    readonly gaveConsent: boolean;
}

interface AvInteractEvent extends AvEvent {
    readonly action: string;
    readonly id: string;
}

interface AvConvertEvent extends AvEvent {
    readonly action: string;
    readonly transactionId: string;
    readonly id: string | undefined;
    readonly price: number | undefined;
}

interface AvTopicsEvent extends AvEvent {
    readonly configVersion: string;
    readonly modelVersion: string;
    readonly taxonomyVersion: string;
    readonly topics: number[];
}

class AvLogger {
    public log(...data: any[]) {
        console.log(...data);
    }
    public warn(...data: any[]) {
        console.warn(...data);
    }
    public error(...data: any[]) {
        console.error(...data);
    }
}

class AvEvents {
    push(event: AvEvent) {
        if (!this._clientName && event.clientName) {
            this._clientName = event.clientName.toLowerCase();
        }
        switch (event.type) {
            case AvEventTypes.Consent:
                this.processConsentEvent(event as AvConsentEvent);
                break;
            default:
                this._events.push(event);
                break;
        }
        setTimeout(() => {
            if (this._clientName && (this._gaveConsent === true /*|| (!this.shouldGiveConsent && this._gaveConsent !== false)*/)) {
                var length = this._events.length;
                if (length) {
                    var events = this._events.splice(0, length);
                    this.processEventsAsync(...events);
                }
            }
        }, 1_000);
    }
    shift?: () => AvEvent | undefined;
    length: number = 0;

    public readonly timeZone: string;
    public readonly shouldGiveConsent: boolean;
    public readonly fledgeEnabled: boolean;
    public readonly topicsEnabled: boolean;
    public readonly logger?: AvLogger;
    public readonly fledgeId: string;

    constructor() {
        if (new URLSearchParams(window.location.search).has("verbose")) {
            this.logger = new AvLogger();
        }
        const EU_TIMEZONES = [
            'Europe/Vienna',
            'Europe/Brussels',
            'Europe/Sofia',
            'Europe/Zagreb',
            'Asia/Famagusta',
            'Asia/Nicosia',
            'Europe/Prague',
            'Europe/Copenhagen',
            'Europe/Tallinn',
            'Europe/Helsinki',
            'Europe/Paris',
            'Europe/Berlin',
            'Europe/Busingen',
            'Europe/Athens',
            'Europe/Budapest',
            'Europe/Dublin',
            'Europe/Rome',
            'Europe/Riga',
            'Europe/Vilnius',
            'Europe/Luxembourg',
            'Europe/Malta',
            'Europe/Amsterdam',
            'Europe/Warsaw',
            'Atlantic/Azores',
            'Atlantic/Madeira',
            'Europe/Lisbon',
            'Europe/Bucharest',
            'Europe/Bratislava',
            'Europe/Ljubljana',
            'Africa/Ceuta',
            'Atlantic/Canary',
            'Europe/Madrid',
            'Europe/Stockholm'
        ];
        this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.shouldGiveConsent = EU_TIMEZONES.indexOf(this.timeZone) >= 0;
        this.fledgeEnabled = 'joinAdInterestGroup' in navigator &&
            document.featurePolicy.allowsFeature('join-ad-interest-group');
        this.topicsEnabled = 'browsingTopics' in document &&
            document.featurePolicy.allowsFeature('browsing-topics') &&
            document.featurePolicy.allowsFeature('interest-cohort');
        this.fledgeId = window.localStorage.getItem("fid") ?? crypto.randomUUID();
        this.logger?.log('AntVoice Fledge IFrame: ' + JSON.stringify({ timeZone: this.timeZone, shouldGiveConsent: this.shouldGiveConsent, fledgeEnabled: this.fledgeEnabled, topicsEnabled: this.topicsEnabled, fledgeId: this.fledgeId }));
    }

    private _events: AvEvent[] = [];
    private _clientName?: string;
    private _gaveConsent?: boolean;

    private processConsentEvent(event: AvConsentEvent) {
        this.logger?.log('AntVoice Consent: ' + JSON.stringify(event));
        this._gaveConsent = event.gaveConsent;
        if (this._gaveConsent === false) {
            window.localStorage.removeItem("fid");
            if (navigator.clearOriginJoinedAdInterestGroups) {
                navigator.clearOriginJoinedAdInterestGroups("https://ps.avads.net");
            }
        } else {
            if (window.localStorage.getItem("fid") == null) {
                window.localStorage.setItem("fid", this.fledgeId);
            }
        }
    }

    private async processEventsAsync(...events: AvEvent[]): Promise<void> {
        try {
            this.logger?.log('AntVoice Events: ' + JSON.stringify(events));
            const requestInit: RequestInit = this.logger ?
                {
                    browsingTopics: this.topicsEnabled,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ id: this.fledgeId, events: events })
                } : {
                    browsingTopics: this.topicsEnabled,
                    method: 'POST',
                    headers: {
                        'Content-Encoding': 'gzip',
                        'Content-Type': 'application/json',
                    },
                    body: await this.gzipAsync(JSON.stringify({ id: this.fledgeId, events: events }))
                };
            const response = await fetch("https://ps.avads.net/join" + (this.logger ? '?verbose' : ''), requestInit);
            if (response.status == 200) {
                var groups = await response.json() as AuctionAdInterestGroup[];
                for (var group of groups) {
                    this.logger?.log('AntVoice Join: ' + JSON.stringify({ owner: group.owner, name: group.name }));
                    await navigator.joinAdInterestGroup(group);
                }
            }
        } catch (exception) {
            await this.logExceptionAsync(exception);
        }
    }

    private async logExceptionAsync(exception: any): Promise<void> {
        var body = JSON.stringify({ Name: exception.name, Message: exception.message, Stack: exception.stack }, null, 4);
        this.logger?.log('AntVoice Exception: \n' + body);
        const requestInit: RequestInit = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: body,
        };
        const response = await fetch("https://ps.avads.net/log", requestInit);
    }

    private gzipAsync(value: string): Promise<Blob> {
        const bytes = new TextEncoder().encode(value);
        const compressionStream = new CompressionStream('gzip');
        const writer = compressionStream.writable.getWriter();
        writer.write(bytes);
        writer.close();
        return new Response(compressionStream.readable).blob();
    }
}

interface Window {
    av_events: AvEvents | AvEvent[];
}

const av_events = window.av_events;
window.av_events = new AvEvents();

if (av_events?.shift) {
    while (av_events.length > 0) {
        let av_event = av_events.shift();
        if (av_event) {
            window.av_events.push(av_event);
        }
    }
}
